import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import { PrimaryButton, WhiteButton } from '../../components/Buttons';
import ErrorMessage from '../../components/ErrorMessage';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, PageTitle, RaisedContainer } from '../../components/Layout';
import LoadingIndicator from '../../components/LoadingIndicator';
import WhiteLabel from '../../filters/WhiteLabel';
import capitalizeFilter from '../../filters/capitalize-filter';
import windowService from '../../services/windowService';
import { logger } from '../../utils/logger';
import { sanitizeHTML } from '../../utils/sanitizeHTML';
import { User, useUser } from '../authentication';
import { ConnectChurchAccountModal, useConnectChurchAccountModalControl } from '../churchAccount';
import { getOrg } from './api/getOrg';

const BUTTON_SIZE = '128px';

const PageContainer = styled(Container)`
  margin: ${Grid._6} auto;
`;

const ButtonsContainer = styled(RaisedContainer)`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${BUTTON_SIZE});
  justify-content: space-evenly;
  gap: ${Grid._5} ${Grid._4};
  padding: ${Grid._7} ${Grid._5};
  margin: ${Grid._5} auto ${Grid._12} auto;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._7} ${Grid._6};
  }
`;

const OrganizationButton = styled(WhiteButton)`
  display: grid;
  grid-template-rows: 1fr 1fr;
  justify-content: center;
  align-items: center;
  background: ${Color.white};
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  width: ${BUTTON_SIZE};
  height: ${BUTTON_SIZE};
  margin: 0;
  padding: ${Grid._4} ${Grid._5};
  white-space: normal;
`;

type OrgNavigationButton = {
  name: string;
  icon: string;
  route: string;
  dataHook: string;
  condition: (user: User) => boolean;
};

const orgNavigationButtons: OrgNavigationButton[] = [
  {
    name: 'CPCA Assignments',
    icon: 'fas fa-chart-bar fa-fw',
    route: '/manage-assignments?cpca=1',
    dataHook: 'organizationManageCPCA',
    condition: user => user.lastSelectedAccount === 'F6E09B28-01C1-4D0B-A184-78748E675629',
  },
  {
    name: 'Manage People',
    icon: 'fas fa-user-friends fa-fw',
    route: '/people',
    dataHook: 'organizationAddPeople',
    condition: user => Boolean(user.permissions?.includes('Roles View')),
  },
  {
    name: 'Search Training',
    icon: 'icon ion-ios-search-strong',
    route: '/search',
    dataHook: 'organizationSearchTraining',
    condition: user => Boolean(user.permissions?.includes('Assign View')),
  },
  {
    name: 'Manage Roles',
    icon: 'fas fa-tags fa-fw',
    route: '/roles',
    dataHook: 'organizationManageRoles',
    condition: user => Boolean(user.permissions?.includes('Account View')),
  },
  {
    name: `Purchases&nbsp;and Subscriptions`,
    icon: 'fas fa-address-card fa-fw',
    route: '/org/purchases-and-subscriptions',
    dataHook: 'organizationManageSubscriptions',
    condition: user => Boolean(user.permissions?.includes('Account View')),
  },
  {
    name: 'Manage Assignments',
    icon: 'fas fa-chart-bar fa-fw',
    route: '/manage-assignments',
    dataHook: 'organizationManageAssignments',
    condition: user => Boolean(user.permissions?.includes('Assign View')),
  },
  {
    name: 'Training Progress',
    icon: 'fas fa-chart-pie fa-fw',
    route: '/training-progress',
    dataHook: 'organizationTrainingProgress',
    condition: user => Boolean(user.permissions?.includes('Assign View')),
  },
  {
    name: 'Assessment Results',
    icon: 'icon ion-ios-compose',
    route: '/assessment/results',
    dataHook: 'organizationAssessmentResults',
    condition: user => Boolean(user.permissions?.includes('Assign View')),
  },
  {
    name: 'Organization Settings',
    icon: 'fas fa-cog fa-fw',
    route: '/org/settings',
    dataHook: 'organizationSettings',
    condition: user => Boolean(user.permissions?.includes('Account View')),
  },
  {
    name: 'Advanced Invites',
    icon: 'fas fa-envelope fa-fw',
    route: '/invites',
    dataHook: 'organizationAdvancedInvites',
    condition: user => Boolean(user.permissions?.includes('Roles View')),
  },
  {
    name: 'Manage Training',
    icon: 'icon ion-settings',
    route: '/manage-training/build/courses',
    dataHook: 'organizationBuildTraining',
    condition: user =>
      Boolean(user.permissions?.includes('Build View') || Boolean(user.permissions?.includes('Build Curriculum View'))),
  },
  {
    name: 'Manage Catalog',
    icon: 'fas fa-book fa-fw',
    route: '/manage-catalog',
    dataHook: 'organizationManageCatalog',
    condition: user => Boolean(user.isAuthor),
  },
  {
    name: 'Training Compilations',
    icon: 'fas fa-stream fa-fw',
    route: '/manage-catalog/compilations',
    dataHook: 'organizationTrainingCompilations',
    condition: user => Boolean(user.isAuthor),
  },
  {
    name: 'Author Training',
    icon: 'fas fa-pen-alt fa-fw',
    route: '/manage-training/author/courses',
    dataHook: 'organizationAuthor',
    condition: user => Boolean(user.isAuthor),
  },
  {
    name: 'Manage Curriculum',
    icon: 'fas fa-bible fa-fw',
    route: '/manage-curriculum',
    dataHook: 'organizationManageCurriculum',
    condition: user => Boolean(user.permissions?.includes('Build Curriculum View')),
  },
];

export const OrganizationOverview = () => {
  const { user } = useUser();
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading');
  const [buttons, setButtons] = useState<OrgNavigationButton[]>([]);
  const [shouldShowSyncMessage, setShouldShowSyncMessage] = useState(false);
  const churchAccountModal = useConnectChurchAccountModalControl();

  useEffect(() => {
    if (!user) {
      setStatus('error');
      return;
    }

    setButtons(orgNavigationButtons.filter(button => button.condition(user)));

    if (window.featureToggles.get('AOM_ORG_CONNECTION') === true) {
      getOrg(user.lastSelectedAccount)
        .then(org => {
          setShouldShowSyncMessage(org && !org.churchAccountId);
        })
        .catch(error => {
          logger.error('Error fetching org data:', error);
          // for now, we don't need to error if we don't receive an org
        })
        .finally(() => setStatus('ready'));
    } else {
      setStatus('ready');
    }
  }, [user?.userId, user?.lastSelectedAccount]);

  return (
    <PageContainer data-qa-hook="organizationView">
      <PageTitle>
        <h1>{capitalizeFilter(WhiteLabel.labels.org)} Management</h1>
      </PageTitle>

      {status === 'loading' && <LoadingIndicator />}
      {status === 'error' && <ErrorMessage />}
      {status === 'ready' && (
        <>
          {shouldShowSyncMessage && (
            <HelpAndInfoBox
              title="Sync Church Account"
              description="This step connects a Lifeway church account to a Ministry Grid church for an improved experience."
            >
              <PrimaryButton onClick={churchAccountModal.open} data-tracking-id="open-sync-church-account-modal">
                Sync Account
              </PrimaryButton>
            </HelpAndInfoBox>
          )}

          <ButtonsContainer>
            {buttons.map(button => (
              <OrganizationButton
                key={button.name}
                data-qa-hook={button.dataHook}
                onClick={() => windowService.redirectTo(`${button.route}`)}
                className="btn btn-dashboard"
              >
                <div>
                  <i className={button.icon}></i>
                </div>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(button.name) }}></div>
              </OrganizationButton>
            ))}
          </ButtonsContainer>

          {churchAccountModal.isOpen && (
            <ConnectChurchAccountModal
              preSelectedAccountId={churchAccountModal.churchAccountId}
              onSuccess={() => setShouldShowSyncMessage(false)}
              dismissHandler={churchAccountModal.close}
              redirect={{ action: 'link', redirectPath: window.location.hash }}
            />
          )}
        </>
      )}
    </PageContainer>
  );
};
